/**
 * Second Person.
 *
 * The mirror selfie without the phone.
 *
 * This file is the conductor. It owns the camera, the short frame buffer, the
 * live coach, and the one piece of state that matters — which of five things
 * the app is currently doing. Finding and erasing the phone live in `erase/`
 * and `ml/`, the pictures in `app/render.ts`, the sheets in `app/panels.ts`.
 */
import './styles/tokens.css';
import './styles/app.css';

import { Camera, CameraError } from './camera/camera';
import { FrameBuffer } from './camera/buffer';
import { importClip } from './camera/import';
import { BURST, QUALITY, type QualityName } from './app/quality';
import { processFrame, sharpest, type CaptureResult, type Source } from './app/capture';
import { Composer, toDisplay, type RenderOptions } from './app/render';
import { Coach, type CoachState } from './app/coach';
import { SettingsStore, type Style } from './app/settings';
import { SheetHost } from './app/sheet';
import { mountGalleryPanel, mountSettingsPanel } from './app/panels';
import { applyTheme, watchSystemTheme } from './app/theme';
import { haptic, mountToasts, playShutter, setHapticsEnabled, setShutterSoundEnabled, toast } from './app/feedback';
import { deleteShot, isGalleryAvailable, requestPersistence, saveShot } from './app/gallery';
import { shareImage, toBlob } from './app/share';
import { fillHole, plannedEraser, prepareEraser } from './ml/inpaint';
import { warmVision } from './ml/tasks';
import { mk, drawCtx } from './vision/image';
import { all, clamp, el, on, setHidden, setPressed } from './util/dom';

type Mode = 'live' | 'counting' | 'busy' | 'result' | 'brush';

// ── elements ──────────────────────────────────────────────────
const stage = el('stage');
const video = el<HTMLVideoElement>('vid');
const coachLayer = el<HTMLCanvasElement>('coachLayer');
const shotCanvas = el<HTMLCanvasElement>('shot');
const wipeCanvas = el<HTMLCanvasElement>('wipe');
const brushLayer = el<HTMLCanvasElement>('brushLayer');
const handle = el('handle');
const recDot = el('recdot');
const recText = el('rectxt');
const note = el('note');
const veil = el('veil');
const veilMessage = el('vmsg');
const veilFill = el('fill');
const countdown = el('countdown');
const countNum = el('countNum');
const blocker = el('blocker');
const blockerTitle = el('blockerTitle');
const blockerBody = el('blockerBody');
const blockerAction = el<HTMLButtonElement>('blockerAction');
const styleRow = el('styleRow');
const finishRow = el('finish');
const brushButton = el<HTMLButtonElement>('brushBtn');
const undoButton = el<HTMLButtonElement>('undoBtn');
const shutter = el<HTMLButtonElement>('shutter');
const saveButton = el<HTMLButtonElement>('saveBtn');
const leftButton = el<HTMLButtonElement>('leftBtn');
const fileInput = el<HTMLInputElement>('filein');
const torchButton = el<HTMLButtonElement>('torchBtn');
const flipButton = el<HTMLButtonElement>('flipBtn');
const zoomWrap = el('zoomWrap');
const zoomInput = el<HTMLInputElement>('zoom');
const focusRing = el('focusRing');

// ── state ─────────────────────────────────────────────────────
const settings = new SettingsStore();
const sheets = new SheetHost(el('scrim'));
const camera = new Camera(video);
const quality = () => QUALITY[settings.get('quality')];
const buffer = new FrameBuffer({
  capacity: quality().frames,
  interval: quality().interval,
  longEdge: quality().longEdge,
});

let mode: Mode = 'live';
let composer: Composer | null = null;
let resultCanvas: HTMLCanvasElement | null = null;
let undoStack: HTMLCanvasElement[] = [];
let torchOn = false;
let countTimer: number | null = null;

const coach = new Coach({
  video,
  overlay: coachLayer,
  onState: paintCoach,
  onRaise: () => {
    if (mode === 'live' && settings.get('style') === 'free') startCountdown();
  },
});

/**
 * Un-mirroring for an imported photograph, decided per photograph. Someone
 * fixing a picture they already have expects it back as it went in, minus the
 * phone — not flipped — so imports open unflipped, and the button on the
 * result flips that one picture. The camera's own frames follow the setting.
 * (This used to switch the setting itself off on import, which quietly changed
 * the next camera photograph too.)
 */
let importedUnmirror = false;

function unmirrorNow(): boolean {
  return composer && composer.capture.source !== 'camera' ? importedUnmirror : settings.get('unmirror');
}

function renderOptions(): RenderOptions {
  return {
    autoLevel: settings.get('autoLevel'),
    unmirror: unmirrorNow(),
    focalPlane: settings.get('focalPlane'),
    watermark: settings.get('watermark'),
  };
}

function setMode(next: Mode): void {
  mode = next;
  const showing = next === 'result' || next === 'brush';
  stage.dataset.mode = showing ? 'result' : 'live';
  stage.dataset.brush = String(next === 'brush');
  setHidden(veil, next !== 'busy');
  setHidden(countdown, next !== 'counting');
  setHidden(handle, next !== 'result');
  setHidden(brushLayer, next !== 'brush');
  setHidden(finishRow, !showing);
  setHidden(styleRow, showing || next === 'busy');
  shutter.disabled = next === 'busy' || next === 'brush';
  saveButton.disabled = next !== 'result';
  shutter.setAttribute('aria-label', shutterLabel());
  shutter.dataset.style = settings.get('style');
  leftButton.textContent = showing ? 'Retake' : next === 'counting' ? 'Cancel' : 'Import';
  leftButton.disabled = next === 'busy' || next === 'brush';
  paintBrushControls();

  // The coach only has something to say about a live frame.
  if (next === 'live' && camera.isLive) coach.start();
  else coach.stop();
}

function shutterLabel(): string {
  if (mode === 'result') return 'Take another photograph';
  return settings.get('style') === 'free' ? `Start the ${settings.get('timer')}-second timer` : 'Take the photograph';
}

function setNote(html: string, tone: CoachState['tone'] = 'neutral'): void {
  note.innerHTML = html;
  note.dataset.tone = tone;
}

function progress(value: number, label?: string): void {
  veilFill.style.width = `${Math.round(clamp(value, 0, 1) * 100)}%`;
  if (label) veilMessage.textContent = label;
}

// ── the coach ─────────────────────────────────────────────────
let lastAdvice = '';
function paintCoach(state: CoachState): void {
  if (mode !== 'live') return;
  // Advice that flickers between two sentences is worse than none; only
  // change the line when the substance has.
  if (state.message === lastAdvice) return;
  lastAdvice = state.message;
  setNote(state.message, state.tone);
  recDot.dataset.state = state.ready ? 'ready' : 'filling';
}

// ── camera ────────────────────────────────────────────────────
function showBlocker(title: string, body: string, actionLabel: string, action: () => void): void {
  blockerTitle.textContent = title;
  blockerBody.textContent = body;
  blockerAction.textContent = actionLabel;
  blockerAction.onclick = action;
  setHidden(blocker, false);
}

function hideBlocker(): void {
  setHidden(blocker, true);
}

async function startCamera(deviceId: string | null = null, facing = camera.facing): Promise<void> {
  try {
    await camera.start(facing, deviceId);
    hideBlocker();
    paintCameraTools();
    buffer.configure({ capacity: quality().frames, interval: quality().interval, longEdge: quality().longEdge });
    buffer.start(video);
    recText.textContent = 'Live';
    warmVision();
    scheduleEraserPrefetch();
    if (mode === 'live') coach.start();
  } catch (err) {
    const message = err instanceof CameraError ? err.message : 'The camera could not be started.';
    const hint =
      err instanceof CameraError && err.kind === 'denied'
        ? 'Allow the camera for this site in your browser settings, then try again. You can still fix photographs you already have with Import.'
        : 'You can still fix photographs you already have with Import.';
    showBlocker('The camera is not running', `${message} ${hint}`, 'Try again', () => {
      void startCamera(deviceId, facing);
    });
    recText.textContent = 'Camera unavailable';
  }
}

function paintCameraTools(): void {
  const caps = camera.capabilities();
  setHidden(torchButton, !caps.torch);
  setHidden(zoomWrap, !caps.zoom);
  if (caps.zoom) {
    zoomInput.min = String(caps.zoom.min);
    zoomInput.max = String(caps.zoom.max);
    zoomInput.step = String(caps.zoom.step || 0.1);
    zoomInput.value = String(camera.zoom);
  }
  if (!caps.torch) {
    torchOn = false;
    setPressed(torchButton, false);
  }
}

/**
 * Fetch the eraser in the background once the camera is up — but only on a
 * connection that is not asking to save data. On cellular it waits for the
 * first photograph that needs it, and says how big it is when it does.
 */
function scheduleEraserPrefetch(): void {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; type?: string } }).connection;
  if (connection?.saveData || connection?.type === 'cellular') return;
  window.setTimeout(() => void prepareEraser().catch(() => undefined), 4000);
}

// ── taking the photograph ─────────────────────────────────────
function takeFrame(): HTMLCanvasElement | null {
  const frames = buffer.snapshot();
  if (frames.length === 0) return null;
  return sharpest(frames.slice(-BURST));
}

async function shoot(): Promise<void> {
  if (mode !== 'live' && mode !== 'counting') return;
  const frame = takeFrame();
  if (!frame) {
    setNote(
      camera.isLive ? '<b>One moment.</b> The camera is still warming up.' : '<b>The camera is not running.</b> You can fix a photograph you already have with Import.',
      'warn',
    );
    haptic('warn');
    setMode('live');
    return;
  }
  playShutter();
  haptic('shutter');
  await process(frame, 'camera');
}

function startCountdown(): void {
  if (mode !== 'live') return;
  let left = settings.get('timer');
  setMode('counting');
  countNum.textContent = String(left);
  haptic('tap');
  countTimer = window.setInterval(() => {
    left -= 1;
    if (left > 0) {
      countNum.textContent = String(left);
      haptic('tap');
      return;
    }
    stopCountdown();
    // Let a few fresh frames arrive after the last beep, so the sharpest-of
    // choice is made over the moment the person was actually holding still.
    window.setTimeout(() => void shoot(), quality().interval * BURST);
  }, 1000);
}

function stopCountdown(): void {
  if (countTimer !== null) window.clearInterval(countTimer);
  countTimer = null;
  coach.rearm();
}

async function process(frame: HTMLCanvasElement, source: Source, takenAt = Date.now()): Promise<void> {
  buffer.setPaused(true);
  setMode('busy');
  progress(0.04, 'Reading');
  try {
    const result = await processFrame(frame, {
      source,
      takenAt,
      onStage: (s) => progress(s.value, s.label),
    });
    composer = new Composer(result);
    undoStack = [];
    await showResult(result);
    haptic(result.removed.length > 0 ? 'done' : 'warn');
  } catch (err) {
    setMode('live');
    buffer.setPaused(false);
    toast(err instanceof Error ? err.message : 'That photograph could not be finished.', 'warn');
    haptic('warn');
  }
}

/** What happened, in a sentence, with the one useful next step when there is one. */
function describe(result: CaptureResult): { html: string; tone: CoachState['tone'] } {
  const levelled =
    settings.get('autoLevel') && result.tiltDegrees ? ` Levelled ${Math.abs(result.tiltDegrees).toFixed(1)}°.` : '';
  if (result.removed.length > 0 && result.refused.length === 0) {
    const what = result.removed.length === 1 ? 'Phone removed.' : `${result.removed.length} phones removed.`;
    return { html: `<b>${what}</b> Drag to compare. Anything it missed, paint over with Brush.${levelled}`, tone: 'good' };
  }
  if (result.refused.length > 0) {
    const also = result.removed.length > 0 ? ' The others are gone.' : '';
    return {
      html: `<b>Your phone is covering your face, so it was left in.</b> Any face painted there would be invented, not yours. Hold the phone at your chest and take another.${also}`,
      tone: 'bad',
    };
  }
  if (result.source === 'camera' && settings.get('style') === 'free') {
    return { html: `<b>No phone in the picture.</b> Nothing to remove.${levelled}`, tone: 'good' };
  }
  return { html: `<b>No phone found.</b> If there is one, tap Brush and paint over it.${levelled}`, tone: 'warn' };
}

async function showResult(result: CaptureResult): Promise<void> {
  if (!composer) return;
  const options = renderOptions();
  resultCanvas = await composer.render(options);
  const before = composer.before(options);
  paintCanvas(shotCanvas, resultCanvas);
  paintCanvas(wipeCanvas, before);

  setMode('result');
  setWipe(result.removed.length > 0 ? 0.5 : 0);
  paintFinishButtons();
  const { html, tone } = describe(result);
  setNote(html, tone);

  if (settings.get('keepGallery') && isGalleryAvailable()) void keep(result, before);
}

function paintCanvas(target: HTMLCanvasElement, source: HTMLCanvasElement): void {
  target.width = source.width;
  target.height = source.height;
  target.getContext('2d')?.drawImage(source, 0, 0);
}

/**
 * Keep receiving a pointer's moves when it leaves the element. Best effort:
 * it throws for a pointer the browser no longer considers active, and a stroke
 * or a wipe should never be lost over that.
 */
function capturePointer(target: Element, pointerId: number): void {
  try {
    target.setPointerCapture(pointerId);
  } catch {
    // Already released; the gesture carries on without capture.
  }
}

/** Longest edge of the copy the gallery grid renders. */
const THUMB_EDGE = 480;

function thumbnailOf(source: HTMLCanvasElement): HTMLCanvasElement {
  const scale = Math.min(1, THUMB_EDGE / Math.max(source.width, source.height));
  const thumb = mk(source.width * scale, source.height * scale);
  const ctx = drawCtx(thumb);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, thumb.width, thumb.height);
  return thumb;
}

let keptId: string | null = null;

async function keep(result: CaptureResult, before: HTMLCanvasElement): Promise<void> {
  // Held locally: a quick Retake clears `resultCanvas` while the encodes below
  // are still running, and the shot must be kept as it was taken.
  const after = resultCanvas;
  if (!after) return;
  try {
    const format = settings.get('format');
    const q = settings.get('quality01');
    // The grid renders dozens of these at a hundred pixels wide. Decoding a
    // full-size photograph for each one is memory the phone has better uses for.
    const [afterBlob, beforeBlob, thumb] = await Promise.all([
      toBlob(after, format, q),
      toBlob(before, format, Math.min(q, 0.82)),
      toBlob(thumbnailOf(after), 'image/jpeg', 0.72),
    ]);
    keptId = await saveShot({
      takenAt: result.takenAt,
      after: afterBlob,
      before: beforeBlob,
      thumb,
      width: after.width,
      height: after.height,
      tiltDegrees: result.tiltDegrees,
      removed: result.removed.length,
      refused: result.refused.length,
      strokes: result.strokes,
      source: result.source,
    });
  } catch {
    // The gallery is a convenience. Losing it must never cost the photograph.
    toast('That shot could not be kept on this device.', 'warn');
  }
}

/**
 * Replace the kept copy after an edit, so the gallery holds the photograph as
 * it now is rather than one entry per brush stroke.
 */
async function rekeep(): Promise<void> {
  if (!composer || !settings.get('keepGallery') || !isGalleryAvailable()) return;
  const previous = keptId;
  keptId = null;
  await keep(composer.capture, composer.before(renderOptions()));
  if (previous) await deleteShot(previous).catch(() => undefined);
}

/** Re-compose without re-erasing — a finish changed, or a brush stroke landed. */
async function recompose(): Promise<void> {
  if (!composer || (mode !== 'result' && mode !== 'brush')) return;
  const options = renderOptions();
  resultCanvas = await composer.render(options);
  paintCanvas(shotCanvas, resultCanvas);
  paintCanvas(wipeCanvas, composer.before(options));
}

function paintFinishButtons(): void {
  setPressed(el('depthBtn'), settings.get('focalPlane'));
  setPressed(el('levelBtn'), settings.get('autoLevel'));
  setPressed(el('mirrorBtn'), unmirrorNow());
  paintBrushControls();
}

function paintBrushControls(): void {
  const brushing = mode === 'brush';
  brushButton.textContent = brushing ? 'Erase it' : 'Brush';
  setPressed(brushButton, brushing);
  undoButton.textContent = brushing ? 'Cancel' : 'Undo';
  undoButton.disabled = brushing ? false : undoStack.length === 0;
  for (const b of all<HTMLButtonElement>('#finish .acc')) b.disabled = brushing;
}

// ── before / after wipe ───────────────────────────────────────
function setWipe(fraction: number): void {
  const f = clamp(fraction, 0, 1);
  wipeCanvas.style.clipPath = `inset(0 ${(1 - f) * 100}% 0 0)`;
  handle.style.left = `${f * 100}%`;
}

let dragging = false;
on(stage, 'pointerdown', (e) => {
  if (mode !== 'result') return;
  dragging = true;
  capturePointer(stage, e.pointerId);
  setWipe((e.clientX - stage.getBoundingClientRect().left) / stage.clientWidth);
});
on(stage, 'pointermove', (e) => {
  if (!dragging) return;
  setWipe((e.clientX - stage.getBoundingClientRect().left) / stage.clientWidth);
});
on(stage, 'pointerup', () => {
  dragging = false;
});
on(stage, 'pointercancel', () => {
  dragging = false;
});

// ── the brush ─────────────────────────────────────────────────
/**
 * Paint over anything the detector missed — a phone it did not find, a second
 * phone in the mirror's edge, a case strap — and it is erased the same way.
 *
 * Strokes are drawn on the photograph as it is shown (un-mirrored and
 * straightened), at the photograph's own resolution, then mapped back to the
 * camera frame the eraser works on.
 */
let painting = false;
let painted = false;

function brushRadius(): number {
  return Math.max(6, Math.round(Math.min(brushLayer.width, brushLayer.height) * 0.035));
}

/**
 * Map a pointer to a pixel of a canvas drawn with `object-fit: cover`. Reading
 * the element's box directly is only right when the aspect ratios match; on a
 * 4:3 sensor in a tall viewfinder it is out by the cropped margin.
 */
function pointInCanvas(canvas: HTMLCanvasElement, clientX: number, clientY: number) {
  const rect = canvas.getBoundingClientRect();
  const scale = Math.max(rect.width / canvas.width, rect.height / canvas.height);
  const drawnW = canvas.width * scale;
  const drawnH = canvas.height * scale;
  return {
    x: (clientX - rect.left - (rect.width - drawnW) / 2) / scale,
    y: (clientY - rect.top - (rect.height - drawnH) / 2) / scale,
  };
}

function enterBrush(): void {
  if (!resultCanvas) return;
  brushLayer.width = resultCanvas.width;
  brushLayer.height = resultCanvas.height;
  drawCtx(brushLayer).clearRect(0, 0, brushLayer.width, brushLayer.height);
  painted = false;
  setWipe(0);
  setMode('brush');
  setNote('<b>Paint over anything you want gone,</b> then tap Erase it.', 'neutral');
}

function dab(clientX: number, clientY: number): void {
  const p = pointInCanvas(brushLayer, clientX, clientY);
  const ctx = drawCtx(brushLayer);
  ctx.fillStyle = 'rgba(255, 45, 85, 0.55)';
  ctx.beginPath();
  ctx.arc(p.x, p.y, brushRadius(), 0, Math.PI * 2);
  ctx.fill();
  painted = true;
}

on(brushLayer, 'pointerdown', (e) => {
  if (mode !== 'brush') return;
  painting = true;
  dab(e.clientX, e.clientY);
  capturePointer(brushLayer, e.pointerId);
});
on(brushLayer, 'pointermove', (e) => {
  if (!painting) return;
  // Coalesced events give a continuous stroke on a fast finger.
  const events = e.getCoalescedEvents?.() ?? [e];
  for (const ev of events) dab(ev.clientX, ev.clientY);
});
on(brushLayer, 'pointerup', () => {
  painting = false;
});
on(brushLayer, 'pointercancel', () => {
  painting = false;
});

async function applyBrush(): Promise<void> {
  if (!composer || !painted) {
    setMode('result');
    return;
  }
  const capture = composer.capture;
  const { width: w, height: h } = capture.erased;
  const shown = drawCtx(brushLayer).getImageData(0, 0, brushLayer.width, brushLayer.height).data;

  // Every camera pixel asks where it lands on screen and whether that spot
  // was painted. Inverse-mapping the strokes instead would leave gaps where
  // straightening stretched them.
  const opts = renderOptions();
  const hole = new Uint8Array(w * h);
  let any = false;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const d = toDisplay(x, y, w, h, opts, capture.tiltDegrees);
      const dx = Math.round(d.x);
      const dy = Math.round(d.y);
      if (dx < 0 || dy < 0 || dx >= brushLayer.width || dy >= brushLayer.height) continue;
      if (shown[(dy * brushLayer.width + dx) * 4 + 3] > 0) {
        hole[y * w + x] = 1;
        any = true;
      }
    }
  }
  if (!any) {
    setMode('result');
    return;
  }

  setMode('busy');
  progress(0.3, 'Erasing');
  try {
    await prepareEraser((loaded, total) => progress(0.3 * (loaded / total), 'Downloading the eraser — once'));
    progress(0.5, 'Erasing');
    undoStack.push(capture.erased);
    if (undoStack.length > 6) undoStack.shift();
    const filled = await fillHole(capture.erased, hole);
    capture.erased = filled.canvas;
    capture.strokes += 1;
    composer.invalidateMask();
    setMode('result');
    await recompose();
    setWipe(0.5);
    setNote('<b>Erased.</b> Drag to compare, or Undo.', 'good');
    haptic('done');
    void rekeep();
  } catch (err) {
    undoStack.pop();
    setMode('result');
    toast(err instanceof Error ? err.message : 'That could not be erased.', 'warn');
  }
}

on(brushButton, 'click', () => {
  haptic('tap');
  if (mode === 'brush') void applyBrush();
  else if (mode === 'result') enterBrush();
});

on(undoButton, 'click', async () => {
  haptic('tap');
  if (mode === 'brush') {
    setMode('result');
    setNote('Brush cancelled.', 'neutral');
    return;
  }
  if (!composer || undoStack.length === 0) return;
  composer.capture.erased = undoStack.pop()!;
  composer.capture.strokes = Math.max(0, composer.capture.strokes - 1);
  composer.invalidateMask();
  await recompose();
  paintBrushControls();
  void rekeep();
});

// ── controls ──────────────────────────────────────────────────
on(shutter, 'click', () => {
  if (mode === 'result') retake();
  else if (mode === 'live') {
    if (settings.get('style') === 'free') startCountdown();
    else void shoot();
  }
});

on(el('countCancel'), 'click', () => {
  stopCountdown();
  setMode('live');
});

function retake(): void {
  composer = null;
  resultCanvas = null;
  undoStack = [];
  keptId = null;
  lastAdvice = '';
  setMode('live');
  buffer.setPaused(false);
  // An imported photograph stops the grab timer; coming back to the camera has
  // to start it again or the buffer would never refill.
  if (camera.isLive) buffer.restart();
  setNote('Frame yourself in the mirror.', 'neutral');
  haptic('tap');
}

on(saveButton, 'click', async () => {
  if (!resultCanvas || !composer) return;
  const format = settings.get('format');
  try {
    const blob = await toBlob(resultCanvas, format, settings.get('quality01'));
    const outcome = await shareImage(blob, format, composer.capture.takenAt);
    if (outcome === 'downloaded') toast('Saved to your downloads.', 'good');
    if (outcome !== 'cancelled') haptic('done');
  } catch {
    toast('That photograph could not be saved.', 'warn');
  }
});

on(el('depthBtn'), 'click', async () => {
  const next = !settings.get('focalPlane');
  settings.set('focalPlane', next);
  paintFinishButtons();
  haptic('tap');
  if (next) {
    progress(0.5, 'Finding the focal plane');
    setHidden(veil, false);
  }
  await recompose();
  setHidden(veil, true);
});

on(el('levelBtn'), 'click', () => {
  settings.set('autoLevel', !settings.get('autoLevel'));
  paintFinishButtons();
  composer?.invalidateMask();
  haptic('tap');
  void recompose();
});

on(el('mirrorBtn'), 'click', () => {
  if (composer && composer.capture.source !== 'camera') importedUnmirror = !importedUnmirror;
  else settings.set('unmirror', !settings.get('unmirror'));
  paintFinishButtons();
  composer?.invalidateMask();
  haptic('tap');
  void recompose();
});

for (const button of all<HTMLButtonElement>('#styleSeg button')) {
  on(button, 'click', () => {
    settings.set('style', button.dataset.style as Style);
    haptic('tap');
  });
}

function paintStyle(): void {
  const style = settings.get('style');
  for (const b of all<HTMLButtonElement>('#styleSeg button')) b.setAttribute('aria-checked', String(b.dataset.style === style));
  coach.configure(style, settings.get('raiseToShoot'));
  shutter.dataset.style = style;
  shutter.setAttribute('aria-label', shutterLabel());
  lastAdvice = '';
}

on(flipButton, 'click', async () => {
  haptic('tap');
  await startCamera(null, camera.facing === 'environment' ? 'user' : 'environment');
});

on(torchButton, 'click', async () => {
  torchOn = !torchOn;
  const applied = await camera.setTorch(torchOn);
  if (!applied) {
    torchOn = false;
    toast('This camera will not take a torch instruction.', 'warn');
  }
  setPressed(torchButton, torchOn);
  haptic('tap');
});

on(zoomInput, 'input', () => {
  void camera.setZoom(Number(zoomInput.value));
});

// Tap the live view to focus there.
on(stage, 'click', (e) => {
  if (mode !== 'live' || !camera.isLive) return;
  const target = e.target as HTMLElement;
  if (target.closest('button, input, label')) return;
  const rect = stage.getBoundingClientRect();
  focusRing.style.left = `${e.clientX - rect.left}px`;
  focusRing.style.top = `${e.clientY - rect.top}px`;
  setHidden(focusRing, false);
  window.setTimeout(() => setHidden(focusRing, true), 620);
  void camera.focusAt((e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
});

// ── import ────────────────────────────────────────────────────
on(leftButton, 'click', () => {
  if (mode === 'result') retake();
  else if (mode === 'counting') {
    stopCountdown();
    setMode('live');
  } else fileInput.click();
});

function loadImage(file: File): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      // Photographs from the roll can be 48 megapixels; the eraser works on a
      // crop either way, but the composite and the gallery do not need that.
      const cap = Math.max(quality().longEdge, 2560);
      const scale = Math.min(1, cap / Math.max(img.naturalWidth, img.naturalHeight));
      const c = mk(img.naturalWidth * scale, img.naturalHeight * scale);
      const ctx = drawCtx(c);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be opened as a photograph.'));
    };
    img.src = url;
  });
}

on(fileInput, 'change', async () => {
  const file = fileInput.files?.[0];
  fileInput.value = '';
  if (!file) return;
  buffer.stop();
  setMode('busy');
  progress(0.02, 'Opening');
  try {
    importedUnmirror = false;
    if (file.type.startsWith('video/')) {
      const clip = await importClip(file, {
        frames: 8,
        longEdge: quality().longEdge,
        onProgress: (f) => progress(0.02 + 0.08 * f, 'Reading the clip'),
      });
      await process(sharpest(clip.frames), 'clip', file.lastModified || Date.now());
    } else {
      await process(await loadImage(file), 'photo', file.lastModified || Date.now());
    }
  } catch (err) {
    setMode('live');
    buffer.restart();
    toast(err instanceof Error ? err.message : 'That file could not be read.', 'warn');
  }
});

// ── sheets ────────────────────────────────────────────────────
const settingsPanel = mountSettingsPanel({
  sheets,
  settings,
  onQualityChange: (q: QualityName) => {
    buffer.configure({ capacity: QUALITY[q].frames, interval: QUALITY[q].interval, longEdge: QUALITY[q].longEdge });
    if (mode === 'result') toast('The new size applies to the next photograph.');
  },
  onDeviceChange: (deviceId) => void startCamera(deviceId),
  listDevices: () => camera.devices(),
  currentDeviceId: () => camera.deviceId,
  eraserStatus: async () => {
    const plan = await plannedEraser();
    const mb = Math.round(plan.bytes / 1e6);
    const which =
      plan.model === 'lama'
        ? 'The full eraser, running on your graphics chip.'
        : 'The fast eraser — this browser has no WebGPU, which the full one needs. It is weaker beside hands.';
    return { label: `${which} ${plan.cached ? 'Downloaded and stored on this device.' : `A ${mb} MB download, once.`}`, ready: plan.cached };
  },
  fetchEraser: async (onProgress) => {
    await prepareEraser((loaded, total) => onProgress(loaded / total));
  },
});
const galleryPanel = mountGalleryPanel({ sheets, settings });

on(el('settingsBtn'), 'click', () => settingsPanel.open());
on(el('galleryBtn'), 'click', () => galleryPanel.open());

// ── keyboard ──────────────────────────────────────────────────
on(document, 'keydown', (e) => {
  if (sheets.current) return;
  const target = e.target as HTMLElement;
  if (target.matches('input, select, textarea')) return;

  if (e.key === ' ' || e.key === 'Enter') {
    if (target.closest('button')) return; // let the button do its own job
    e.preventDefault();
    shutter.click();
  } else if (e.key === 'Escape' && mode === 'counting') {
    stopCountdown();
    setMode('live');
  } else if (mode === 'result' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
    e.preventDefault();
    const current = parseFloat(handle.style.left) || 50;
    setWipe((current + (e.key === 'ArrowRight' ? 6 : -6)) / 100);
  }
});

// ── lifecycle ─────────────────────────────────────────────────
on(document, 'visibilitychange', () => {
  if (document.hidden) {
    buffer.setPaused(true);
    if (mode === 'counting') {
      stopCountdown();
      setMode('live');
    }
    return;
  }
  buffer.setPaused(mode !== 'live');
  // iOS ends the tracks when the app is backgrounded and says nothing about it.
  if (mode === 'live' && !camera.isLive) void startCamera(camera.deviceId);
});

on(window, 'pagehide', () => camera.stop());

settings.subscribe((value) => {
  setHapticsEnabled(value.haptics);
  setShutterSoundEnabled(value.shutterSound);
  paintStyle();
});

// ── boot ──────────────────────────────────────────────────────
async function boot(): Promise<void> {
  mountToasts(el('toast'));
  applyTheme(settings.get('theme'));
  watchSystemTheme(() => settings.get('theme'));
  setHapticsEnabled(settings.get('haptics'));
  setShutterSoundEnabled(settings.get('shutterSound'));
  paintStyle();
  paintFinishButtons();
  setMode('live');

  el('edition').textContent = `v${__APP_VERSION__}`;

  if (new URLSearchParams(location.search).get('open') === 'gallery') galleryPanel.open();

  if (!settings.get('onboarded')) {
    const onboard = el('onboardSheet');
    const finish = (start: boolean) => {
      settings.set('onboarded', true);
      sheets.close();
      if (start) void startCamera();
    };
    on(el('onboardStart'), 'click', () => finish(true));
    on(el('onboardSkip'), 'click', () => finish(false));
    sheets.open(onboard);
  } else {
    await startCamera();
  }

  if (settings.get('keepGallery')) void requestPersistence();

  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    navigator.serviceWorker
      .register(`${import.meta.env.BASE_URL}sw.js`, { scope: import.meta.env.BASE_URL })
      .catch(() => undefined);
  }
}

void boot();
